import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Procedural tree that grows with L-system-style branching
class ProceduralTree {
  constructor(scene, x, z, opts = {}) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.position.set(x, 0, z);
    this.scene.add(this.group);

    this.maxDepth = opts.depth || 5;
    this.growthProgress = 0; // 0 to 1
    this.growthSpeed = opts.speed || (0.15 + Math.random() * 0.1);
    this.seed = opts.seed || Math.random() * 1000;
    this.trunkHeight = opts.trunkHeight || (2.5 + Math.random() * 1.5);
    this.branchAngle = opts.branchAngle || (0.4 + Math.random() * 0.3);
    this.scale = opts.scale || (0.6 + Math.random() * 0.5);
    this.group.scale.setScalar(this.scale);

    // Colors
    this.barkColor = new THREE.Color(0x2d1f0e);
    this.leafColors = [
      new THREE.Color(0x4ade80),
      new THREE.Color(0x22c55e),
      new THREE.Color(0xa3e635),
      new THREE.Color(0x16a34a),
      new THREE.Color(0x86efac),
    ];

    this.branches = [];
    this.leaves = [];
    this.built = false;
  }

  seededRandom(i) {
    const x = Math.sin(this.seed + i * 127.1) * 43758.5453;
    return x - Math.floor(x);
  }

  build() {
    if (this.built) return;
    this.built = true;
    this.generateBranches(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 1, 0),
      this.trunkHeight,
      0.12,
      0,
      0
    );
  }

  generateBranches(origin, direction, length, radius, depth, index) {
    if (depth > this.maxDepth) return;

    const segments = 6;
    const points = [];
    const current = origin.clone();

    for (let i = 0; i <= segments; i++) {
      points.push(current.clone());
      const step = direction.clone().multiplyScalar(length / segments);
      // Add slight randomness to growth direction
      step.x += (this.seededRandom(index * 100 + i * 7) - 0.5) * 0.15;
      step.z += (this.seededRandom(index * 100 + i * 13) - 0.5) * 0.15;
      current.add(step);
    }

    const curve = new THREE.CatmullRomCurve3(points);
    const tubeGeo = new THREE.TubeGeometry(curve, 8, radius, 5, false);
    const barkMat = new THREE.MeshLambertMaterial({
      color: this.barkColor,
      transparent: true,
      opacity: 0
    });
    const branch = new THREE.Mesh(tubeGeo, barkMat);
    this.group.add(branch);

    // Store branch with metadata for growth animation
    const depthRatio = depth / this.maxDepth;
    this.branches.push({
      mesh: branch,
      growStart: depthRatio * 0.6, // deeper branches appear later
      growEnd: depthRatio * 0.6 + 0.4,
    });

    // Add leaves at tips (depth >= 2)
    if (depth >= 2) {
      const tipPos = points[points.length - 1];
      const numLeaves = depth >= 4 ? 3 : 5;
      for (let l = 0; l < numLeaves; l++) {
        const leafColor = this.leafColors[Math.floor(this.seededRandom(index * 50 + l) * this.leafColors.length)];
        const leafSize = (0.15 + this.seededRandom(index * 30 + l) * 0.25) * (1 - depthRatio * 0.3);
        const leafGeo = new THREE.SphereGeometry(leafSize, 5, 4);
        const leafMat = new THREE.MeshLambertMaterial({
          color: leafColor,
          transparent: true,
          opacity: 0,
          emissive: leafColor,
          emissiveIntensity: 0.15
        });
        const leaf = new THREE.Mesh(leafGeo, leafMat);
        leaf.position.copy(tipPos);
        leaf.position.x += (this.seededRandom(index * 40 + l * 3) - 0.5) * 0.4;
        leaf.position.y += (this.seededRandom(index * 40 + l * 5) - 0.5) * 0.3;
        leaf.position.z += (this.seededRandom(index * 40 + l * 7) - 0.5) * 0.4;
        this.group.add(leaf);
        this.leaves.push({
          mesh: leaf,
          growStart: depthRatio * 0.6 + 0.3,
          growEnd: depthRatio * 0.6 + 0.7,
          baseY: leaf.position.y,
          phase: this.seededRandom(index * 60 + l) * Math.PI * 2,
        });
      }
    }

    // Recursively branch
    if (depth < this.maxDepth) {
      const numChildren = depth === 0 ? 3 : 2;
      const tip = points[points.length - 1];

      for (let c = 0; c < numChildren; c++) {
        const angle = this.branchAngle + (this.seededRandom(index * 200 + c * 31) - 0.5) * 0.3;
        const rotAxis = new THREE.Vector3(
          this.seededRandom(index * 200 + c * 17) - 0.5,
          0.2,
          this.seededRandom(index * 200 + c * 23) - 0.5
        ).normalize();

        const newDir = direction.clone().applyAxisAngle(rotAxis, angle).normalize();
        const newLength = length * (0.65 + this.seededRandom(index * 200 + c * 41) * 0.15);
        const newRadius = radius * 0.65;

        this.generateBranches(tip, newDir, newLength, newRadius, depth + 1, index * 10 + c + 1);
      }
    }
  }

  update(time) {
    // Grow over time
    if (this.growthProgress < 1) {
      this.growthProgress = Math.min(1, this.growthProgress + this.growthSpeed * 0.008);
    }

    const p = this.growthProgress;

    // Animate branches appearing
    for (const b of this.branches) {
      const t = Math.max(0, Math.min(1, (p - b.growStart) / (b.growEnd - b.growStart)));
      b.mesh.material.opacity = t;
      b.mesh.scale.setScalar(0.5 + t * 0.5);
    }

    // Animate leaves
    for (const l of this.leaves) {
      const t = Math.max(0, Math.min(1, (p - l.growStart) / (l.growEnd - l.growStart)));
      l.mesh.material.opacity = t * 0.85;
      l.mesh.scale.setScalar(t);
      // Gentle sway
      l.mesh.position.y = l.baseY + Math.sin(time * 1.5 + l.phase) * 0.05;
      l.mesh.position.x += Math.sin(time * 0.8 + l.phase * 2) * 0.001;
    }

    // Gentle trunk sway
    this.group.rotation.z = Math.sin(time * 0.4 + this.seed) * 0.02;
    this.group.rotation.x = Math.sin(time * 0.3 + this.seed * 2) * 0.01;
  }

  dispose() {
    this.group.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
    this.scene.remove(this.group);
  }
}

// Floating particle (spore/pollen)
class Particles {
  constructor(scene, count = 80) {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    this.velocities = [];

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 30;
      positions[i * 3 + 1] = Math.random() * 15;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
      sizes[i] = 0.03 + Math.random() * 0.06;
      this.velocities.push({
        x: (Math.random() - 0.5) * 0.01,
        y: 0.005 + Math.random() * 0.01,
        z: (Math.random() - 0.5) * 0.01,
      });
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const mat = new THREE.PointsMaterial({
      color: 0xa3e635,
      size: 0.08,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    this.points = new THREE.Points(geo, mat);
    scene.add(this.points);
    this.count = count;
  }

  update(time) {
    const positions = this.points.geometry.attributes.position.array;
    for (let i = 0; i < this.count; i++) {
      const v = this.velocities[i];
      positions[i * 3] += v.x + Math.sin(time + i) * 0.003;
      positions[i * 3 + 1] += v.y;
      positions[i * 3 + 2] += v.z + Math.cos(time + i * 0.7) * 0.002;

      // Reset if too high
      if (positions[i * 3 + 1] > 16) {
        positions[i * 3] = (Math.random() - 0.5) * 30;
        positions[i * 3 + 1] = -1;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
      }
    }
    this.points.geometry.attributes.position.needsUpdate = true;
    this.points.material.opacity = 0.4 + Math.sin(time * 0.5) * 0.2;
  }

  dispose() {
    this.points.geometry.dispose();
    this.points.material.dispose();
  }
}

export default function LiquidGradient() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance'
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = false;
    container.appendChild(renderer.domElement);

    // Scene
    const scene = new THREE.Scene();
    // Dark gradient background via fog + background color
    scene.background = new THREE.Color(0x0a1410);
    scene.fog = new THREE.FogExp2(0x0a1410, 0.04);

    // Camera
    const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 200);
    camera.position.set(0, 4, 14);
    camera.lookAt(0, 3, 0);

    // Lighting
    const ambient = new THREE.AmbientLight(0x1a3a2a, 0.8);
    scene.add(ambient);

    const moonlight = new THREE.DirectionalLight(0x88ccaa, 0.6);
    moonlight.position.set(-5, 12, 8);
    scene.add(moonlight);

    const rimLight = new THREE.DirectionalLight(0xa3e635, 0.3);
    rimLight.position.set(5, 8, -5);
    scene.add(rimLight);

    // Ground plane (subtle)
    const groundGeo = new THREE.PlaneGeometry(60, 60);
    const groundMat = new THREE.MeshLambertMaterial({
      color: 0x0d1f15,
      transparent: true,
      opacity: 0.8
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.1;
    scene.add(ground);

    // Create trees at various positions
    const trees = [];
    const treePositions = [
      { x: 0, z: 0, depth: 5, scale: 1.0, speed: 0.12 },
      { x: -4, z: -2, depth: 4, scale: 0.7, speed: 0.15 },
      { x: 4.5, z: -1, depth: 4, scale: 0.8, speed: 0.14 },
      { x: -7, z: -4, depth: 3, scale: 0.5, speed: 0.18 },
      { x: 7, z: -3, depth: 3, scale: 0.55, speed: 0.17 },
      { x: -2, z: -5, depth: 4, scale: 0.65, speed: 0.13 },
      { x: 3, z: -6, depth: 3, scale: 0.5, speed: 0.16 },
      { x: -9, z: -1, depth: 3, scale: 0.45, speed: 0.2 },
      { x: 9, z: -5, depth: 3, scale: 0.4, speed: 0.19 },
    ];

    // Stagger tree growth start
    treePositions.forEach((pos, i) => {
      const tree = new ProceduralTree(scene, pos.x, pos.z, {
        depth: pos.depth,
        scale: pos.scale,
        speed: pos.speed,
        seed: i * 137.5,
      });
      tree.build();
      // Delay growth start for staggered effect
      tree.growthProgress = -i * 0.08;
      trees.push(tree);
    });

    // Particles
    const particles = new Particles(scene, 100);

    // Animation
    const clock = new THREE.Clock();
    let animId;
    let time = 0;

    function tick() {
      const delta = Math.min(clock.getDelta(), 0.05);
      time += delta;

      // Update trees
      for (const tree of trees) {
        tree.update(time);
      }

      // Update particles
      particles.update(time);

      // Gentle camera drift
      camera.position.x = Math.sin(time * 0.1) * 0.5;
      camera.position.y = 4 + Math.sin(time * 0.15) * 0.3;
      camera.lookAt(0, 3, 0);

      renderer.render(scene, camera);
      animId = requestAnimationFrame(tick);
    }
    tick();

    // Resize
    function onResize() {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
      for (const tree of trees) tree.dispose();
      particles.dispose();
      ground.geometry.dispose();
      ground.material.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="liquid-gradient" />;
}
